import { Grid2, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import { Controller } from "react-hook-form";
import { useTranslate } from "../../../../../hooks/useTranslate";
import TextInput from "../../../../../components/TextInput";
import { useStudentForm } from "../../hooks/useStudentForm";
import Select from "../../../../../components/Select";
import { useGetGenders } from "../../../../../hooks/useGetGenders";
import { useGetSENTypes } from "../../../../../hooks/useGetSENTypes";
import { useGetSafeguardingTypes } from "../../../../../hooks/useGetSafeguardingTypes";
import DatePickerInput from "../../../../../components/DatePickerInput";
import Preloader from "../../../../../components/Preloader";
import { useGetStudentContacts } from "../../../../../hooks/useGetStudentContacts";
import axiosManager from "../../../../../services/axiosManager";
import SnackbarToast from "../../../../../components/SnackbarToast";
import BaseDialog from "../../../../../components/BaseDialog";
import ContactInformationForm from "../../../ResponsibleAdults/components/ContactInformationForm";
import { useGetRelationTypes } from "../../../../../hooks/useGetRelationTypes";
import { capitalizeFirstLetter } from "../../../../../utils/capitalizeFirstLetter";
import FileDropUploader from "../../../../../components/FileDropUploader";
import useVerificationStatus from "../../../../../context/status/useStatuses";
import { getVerificationStatusKeyAndFlag, KEY_NONE, KEY_PROGRESS } from "../../../../../utils/getVerificationStatusKey";
import VerificationStatusChip from "../../../../../components/VerificationStatusChip";
import Autocomplete from "../../../../../components/Autocomplete";
import { useGetLanguages } from "../../../../../hooks/useGetLanguages";
import { useGetNationalities } from "../../../../../hooks/useGetNationalities";
import { isVersionUS } from "../../../../../services/versionService";
import OptionsToggle from "../../../../../components/OptionsToggle";
import RadioOptions from "../../../../../components/RadioOptions";
import CheckboxOptions from "../../../../../components/CheckboxOptions";
import OutOfYearTemplate
    from "../../../../../assets/docs/Out of Year Study Request Form - King's InterHigh  example.docx";
import "./StudentDetailsForm.css";
import { CONTACT_METHOD } from "../../../../../constants/contactMethod";

const MAX_DATE_OF_BIRTH = `${(new Date().getFullYear() - 7)}-01-01`;

const StudentDetailsForm = (props) => {
    const {
        data, onSuccess, isRequiredDBS, isRequiredOutOfYear,
        isDisabled,
    } = props;
    const { t } = useTranslate();

    const { studentVerificationKey } = useVerificationStatus();
    const {
        contactsAddressOptions, contacts, isLoading: isLoadingContacts, refetch, isRefetching,
    } = useGetStudentContacts(data.studentId);
    const { gendersOptions, isLoading: isLoadingGenders } = useGetGenders();
    const { senTypesOptions, isLoading: isLoadingSEN } = useGetSENTypes();
    const { safeguardingTypesOptions, isLoading: isLoadingSafeguarding } = useGetSafeguardingTypes();
    const { languagesOptions, isLoading: isLoadingLanguages } = useGetLanguages();
    const { nationalitiesOptions, isLoading: isLoadingNationalities } = useGetNationalities();
    const { relationshipsOptions, isLoading: isLoadingRelations } = useGetRelationTypes();

    const [showAddContactDialog, setShowAddContactDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [proofOfId, setProofOfId] = useState(null);
    // const [disabilityFile, setDisabilityFile] = useState(null);
    const [senFile, setSenFile] = useState(null);
    const [safeguardingFile, setSafeguardingFile] = useState(null);
    const [dbsCheck, setDbsCheck] = useState(null);
    const [outOfYear, setOutOfYear] = useState(null);

    const dataForm = { students: [{ ...data, isResponsible: true }] };
    const safeguardingFileStatus = useMemo(() => data.safeguardingDocumentStatus && getVerificationStatusKeyAndFlag({ verificationStatus: data.safeguardingDocumentStatus }), [data.safeguardingDocumentStatus]);

    const uploadedSenFile = useMemo(() => (data?.sendDocument ? [data.sendDocument] : null), [data.sendDocument]);
    const uploadedSafeguardingFile = useMemo(() => (data?.safeguardingDocument ? [data.safeguardingDocument] : null), [data.safeguardingDocument]);
    const uploadedDBSFile = useMemo(() => (data?.dbsDocument ? [data.dbsDocument] : null), [data.dbsDocument]);
    const uploadedOutOfYearFile = useMemo(() => (data?.outOfYearDocument ? [data.outOfYearDocument] : null), [data.outOfYearDocument]);

    const {
        register, control, errors,
        isDirtyForm, isSubmitted,
        watch, handleSubmit, setValue, reset,
    } = useStudentForm(data);

    const blocker = useBlocker(isDirtyForm || !!proofOfId);

    const {
        mutateAsync: updateStudent, reset: resetUpdateStudent,
        isPending: isUpdatingStudent, isSuccess: isSuccessUpdateStudent, error: errorUpdateStudent,
    } = useMutation({
        mutationFn: (body) => axiosManager.put$(`/parent/api/students/${data.studentId}`, body),
    });

    const {
        mutateAsync: createContact, reset: resetCreateContact,
        isPending: isCreatingContact, isSuccess: isSuccessCreateContact, error: errorCreateContact,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/contacts?method=${CONTACT_METHOD.ENROLMENT}`, body),
        onSuccess: async (response) => {
            setShowAddContactDialog(false);
            const contactsData = await refetch();
            const newlyCreatedContact = (contactsData.data || []).find((contact) => contact.contactId === response?.data?.contactId);

            setValue("addressId", newlyCreatedContact?.addresses?.[0]?.addressId);
        },
    });

    const {
        mutateAsync: uploadProofOfId, reset: resetUploadProofOfId,
        isPending: isUploadingProofOfId, isSuccess: isSuccessUploadProofOfId, error: errorUploadProofOfId,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${data.studentId}/ProofOfId`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });
    const {
        mutateAsync: uploadDBS, reset: resetDBS,
        isPending: isUploadingDBS, isSuccess: isSuccessUploadDBS, error: errorUploadDBS,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${data.studentId}/DBS`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });
    const {
        mutateAsync: uploadOutOfYear, reset: resetOutOfYear,
        isPending: isUploadingOutOfYear, isSuccess: isSuccessUploadOutOfYear, error: errorUploadOutOfYear,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${data.studentId}/OutOfYearForm`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });

    const {
        mutateAsync: uploadSenFile, reset: resetUploadSenFile,
        isPending: isUploadingSenFile, isSuccess: isSuccessUploadSenFile, error: errorUploadSenFile,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${data.studentId}/SEND`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });

    const {
        mutateAsync: uploadSafeguardingFile, reset: resetUploadSafeguardingFile,
        isPending: isUploadingSafeguardingFile, isSuccess: isSuccessUploadSafeguardingFile, error: errorUploadSafeguardingFile,
    } = useMutation({
        mutationFn: (body) => axiosManager.post$(`/parent/api/students/${data.studentId}/safeguarding`, body, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    });

    const citizenOptions = [
        { value: true, label: t("citizenOfSchoolCountry.yes") },
        { value: false, label: t("citizenOfSchoolCountry.no") },
    ];
    const senOptions = [
        { value: true, title: t("senInformation.yes") },
        { value: false, title: t("senInformation.no") },
    ];

    const addressId = watch("addressId");
    // const disability = watch("disability");
    const sen = watch("hasSEN");
    const safeguarding = watch("safeguarding");
    const safeguardingOption = safeguardingTypesOptions.find((item) => item.value === safeguarding)?.name;
    const selectedContactData = contacts.find((contact) => contact.primaryAddress?.addressId === addressId)?.primaryAddress || {};

    useEffect(() => {
        if (addressId === "new") setShowAddContactDialog(true);
    }, [addressId]);

    const onCloseAddContactDialog = () => {
        setShowAddContactDialog(false);
        setValue("addressId", contactsAddressOptions?.[0]?.value);
    };

    const onSubmit = handleSubmit(async (formData) => {
        const {
            nationality,
            firstLanguage,
            sen,
            ...formDataObj
        } = formData;

        if (safeguardingOption !== "None" && !safeguardingFile && !uploadedSafeguardingFile && !isVersionUS) {
            return;
        }

        const bodyData = {
            ...formDataObj,
            studentId: data.studentId,
            nationality: nationality.label,
            nationalityIsoCode: nationality.id,
            firstLanguage: firstLanguage.label,
            firstLanguageIsoCode: firstLanguage.id,
            sen,
        };

        if (isDirtyForm) {
            resetUpdateStudent();
            await updateStudent(bodyData);
        }
        if (proofOfId) {
            const formDataBody = new FormData();
            formDataBody.append("file", proofOfId);
            await uploadProofOfId(formDataBody);
        }
        if (senFile) {
            const formDataBody = new FormData();
            formDataBody.append("file", senFile);
            await uploadSenFile(formDataBody);
        }
        if (safeguardingFile) {
            const formDataBody = new FormData();
            formDataBody.append("file", safeguardingFile);
            await uploadSafeguardingFile(formDataBody);
        }
        if (dbsCheck) {
            const formDataBody = new FormData();
            formDataBody.append("file", dbsCheck);
            await uploadDBS(formDataBody);
        }
        if (outOfYear) {
            const formDataBody = new FormData();
            formDataBody.append("file", outOfYear);
            await uploadOutOfYear(formDataBody);
        }

        setProofOfId(null);
        onSuccess?.();
    }, console.log);

    const onCreateContact = async (formData) => {
        const { country, ...formDataObj } = formData;
        const bodyData = Object.keys(formDataObj).reduce((acc, key) => {
            acc[capitalizeFirstLetter(key)] = formDataObj[key];
            return acc;
        }, {});

        await createContact({
            ...bodyData,
            CountryOfResidence: country,
        });
    };

    const onChangeProofOfId = (files) => {
        setProofOfId(files?.[0]);
    };
    /* const onChangeDisabilityFile = (files) => {
        setDisabilityFile(files?.[0]);
    }; */
    const onChangeSenFile = (files) => {
        setSenFile(files?.[0]);
    };
    const onChangeDBS = (files) => {
        setDbsCheck(files?.[0]);
    };
    const onChangeOutOfYear = (files) => {
        setOutOfYear(files?.[0]);
    };

    const onCancelDialogClose = () => {
        blocker.reset?.();
        setShowCancelDialog(false);
    };
    const onCancelDialogContinue = () => {
        blocker.proceed?.();
    };

    return (
        <Preloader isLoading={isUpdatingStudent || isUploadingProofOfId || isUploadingDBS || isUploadingOutOfYear || isUploadingSenFile || isUploadingSafeguardingFile} overflow>
            <form className="student-details-form" onSubmit={onSubmit}>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.forename")}
                                    register={register}
                                    name="forename"
                                    error={errors.forename?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.preferredName")}
                                    register={register}
                                    name="preferredName"
                                    error={errors.preferredName?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: isVersionUS ? 5 : 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.middleName")}
                                    register={register}
                                    name="middleNames"
                                    error={errors.middleNames?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: isVersionUS ? 5 : 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.surname")}
                                    register={register}
                                    name="surname"
                                    error={errors.surname?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            {isVersionUS && (
                                <Grid2 size={{ xs: 12, md: 2 }} className="mb-4">
                                    <TextInput
                                        label={t("studentDetails.form.suffix")}
                                        register={register}
                                        name="suffix"
                                        error={errors.suffix?.message}
                                        disabled={isDisabled}
                                    />
                                </Grid2>
                            )}
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <Select
                                    label={t("studentDetails.form.gender")}
                                    control={control}
                                    name="genderTypeId"
                                    disabled={isDisabled || isLoadingGenders}
                                    options={gendersOptions}
                                    error={errors.genderTypeId?.message}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <Autocomplete
                                    label={t("studentDetails.form.nationality")}
                                    control={control}
                                    name="nationality"
                                    options={nationalitiesOptions}
                                    disabled={isDisabled || isLoadingNationalities}
                                    required
                                    error={errors.nationality?.message}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.key}>
                                            {option.name}
                                        </li>
                                    )}
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            {isVersionUS && (
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <Select
                                        label={t("studentDetails.form.USCitizen")}
                                        control={control}
                                        name="citizenOfSchoolCountry"
                                        options={citizenOptions}
                                        error={errors.citizenOfSchoolCountry?.message}
                                        disabled={isDisabled}
                                    />
                                </Grid2>
                            )}
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <DatePickerInput
                                    label={t("studentDetails.form.dateOfBirth")}
                                    control={control}
                                    name="dateOfBirth"
                                    maxDate={MAX_DATE_OF_BIRTH}
                                    error={errors.dateOfBirth?.message}
                                    disabled={isDisabled}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: isVersionUS ? 12 : 6 }} className="mb-4">
                                <Autocomplete
                                    label={t("studentDetails.form.firstLanguage")}
                                    control={control}
                                    name="firstLanguage"
                                    options={languagesOptions}
                                    disabled={isDisabled || isLoadingLanguages}
                                    error={errors.firstLanguage?.message}
                                    required
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Grid2 size={12} className="mb-4">
                            <div className="address-quick-field">
                                <span className="address-quick-field__label">
                                    {t("studentDetails.form.studentsAddress")}
                                    <span className="required-field">*</span>
                                </span>
                                <div className="flex align-center justify-between gap-4">
                                    <span className="address-quick-field__label">{t("studentDetails.form.sameAs")}</span>
                                    <Select
                                        control={control}
                                        name="addressId"
                                        options={contactsAddressOptions}
                                        disabled={isDisabled || isLoadingContacts || isRefetching}
                                        error={errors.addressId?.message}
                                    />
                                </div>
                            </div>
                        </Grid2>
                        <Grid2 size={12} className="mb-4">
                            <TextInput
                                label={t("studentDetails.form.addressLine1")}
                                name="addressLine1"
                                value={selectedContactData.addressLine1}
                                disabled
                            />
                        </Grid2>
                        <Grid2 size={12} className="mb-4">
                            <TextInput
                                label={t("studentDetails.form.addressLine2")}
                                name="addressLine2"
                                value={selectedContactData.addressLine2}
                                disabled
                            />
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.city")}
                                    name="city"
                                    value={selectedContactData.city}
                                    disabled
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.zip")}
                                    name="zip"
                                    value={selectedContactData.code}
                                    disabled
                                />
                            </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.state")}
                                    name="state"
                                    value={selectedContactData.area}
                                    disabled
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                                <TextInput
                                    label={t("studentDetails.form.countryOfResidence")}
                                    name="countryOfResidence"
                                    value={selectedContactData.country}
                                    disabled
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Grid2>
                {!isVersionUS && (
                    <>
                        <div className="divider mt-8 mb-8" />
                        <Typography variant="h4" component="div" className="text-primary">
                            {t("studentDetails.form.senInfoTitle")}
                        </Typography>
                        <Typography variant="body1" component="div" className="">
                            {t("studentDetails.form.senInfoSubtitle")}
                        </Typography>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 8 }} className="mb-4">
                                <FormControl error={!!errors?.hasSEN?.message} className="w-full">
                                    <Controller
                                        name="hasSEN"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <OptionsToggle
                                                label={t("studentDetails.form.specialEducationalNeeds")}
                                                options={senOptions}
                                                name="hasSEN"
                                                value={value}
                                                onChange={onChange}
                                                disabled={isDisabled}
                                                vertical
                                                className="mt-8 w-full"
                                                additionalBlock={sen ? (
                                                    <div className="sen-additional">
                                                        <Typography variant="h5" component="div" className="text-primary">
                                                            {t("studentDetails.form.senSpecify")}
                                                        </Typography>
                                                        <Controller
                                                            name="sen"
                                                            control={control}
                                                            disabled={isLoadingSEN}
                                                            render={({ field }) => (
                                                                <>
                                                                    <CheckboxOptions
                                                                        name="senTypeID"
                                                                        options={senTypesOptions}
                                                                        value={field.value.map((item) => item.senTypeId)}
                                                                        onChange={(selectedIds) => {
                                                                            // Map selected IDs to objects, preserving otherDescription if present
                                                                            const newSen = selectedIds.map((id) => {
                                                                                const existing = field.value.find((item) => item.senTypeId === id);
                                                                                return existing || { senTypeId: id, otherDescription: "" };
                                                                            });
                                                                            field.onChange(newSen);
                                                                        }}
                                                                        error={errors?.sen?.message}
                                                                        disabled={isDisabled}
                                                                    />
                                                                    {field.value
                                                                        .filter((value) => senTypesOptions.find((senType) => senType.value === value.senTypeId)?.label === "Other")
                                                                        .map((value) => (
                                                                            <TextInput
                                                                                key={value.senTypeId}
                                                                                placeholder={t("studentDetails.form.senOtherDescription")}
                                                                                name={`sen[${field.value.findIndex((v) => v.senTypeId === value.senTypeId)}].otherDescription`}
                                                                                value={value.otherDescription || ""}
                                                                                disabled={isDisabled}
                                                                                onChange={(e) => {
                                                                                    const updated = field.value.map((item) => (
                                                                                        item.senTypeId === value.senTypeId
                                                                                            ? { ...item, otherDescription: e.target.value }
                                                                                            : item
                                                                                    ));
                                                                                    field.onChange(updated);
                                                                                }}
                                                                                error={errors?.sen?.[field.value.findIndex((v) => v.senTypeId === value.senTypeId)]?.otherDescription?.message}
                                                                            />
                                                                        ))}
                                                                </>
                                                            )}
                                                        />
                                                    </div>
                                                ) : null}
                                            />
                                        )}
                                    />
                                    {errors?.hasSEN?.message ? <FormHelperText>{errors?.hasSEN?.message}</FormHelperText> : null}
                                </FormControl>
                                {sen && (
                                    <>
                                        <Typography variant="body1" component="div" className="mt-8">
                                            {t("studentDetails.form.senFileUpload")}
                                        </Typography>
                                        <Controller
                                            name="sendDocument"
                                            control={control}
                                            disabled={isLoadingSEN}
                                            render={({ field }) => (
                                                <FileDropUploader
                                                    className="mt-4"
                                                    accept={{
                                                        "image/jpeg": [".png", ".jpg", ".jpeg"],
                                                        "application/pdf": [".pdf"],
                                                        "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                                    }}
                                                    uploadedFiles={uploadedSenFile}
                                                    showFiles
                                                    onChange={onChangeSenFile}
                                                    disabled={isDisabled || isUploadingSenFile}
                                                />
                                            )}
                                        />
                                    </>
                                )}
                                {/* <FormControl error={!!errors?.disability?.message} className="w-full">
                                    <Controller
                                        name="disability"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <OptionsToggle
                                                label={t("studentDetails.form.childDisability")}
                                                options={senOptions}
                                                value={value}
                                                onChange={onChange}
                                                vertical
                                                className="mt-8 w-full"
                                            />
                                        )}
                                    />
                                    {errors?.disability?.message ? <FormHelperText>{errors?.disability?.message}</FormHelperText> : null}
                                </FormControl> */}
                                {/* {disability && (
                                    <>
                                        <Typography variant="body1" components="div" className="mt-8">
                                            {t("studentDetails.form.childDisabilityUpload")}
                                        </Typography>
                                        <FileDropUploader
                                            className="mt-4"
                                            accept={{
                                                "image/jpeg": [".png", ".jpg", ".jpeg"],
                                                "application/pdf": [".pdf"],
                                                "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                            }}
                                            showFiles
                                            onChange={onChangeDisabilityFile}
                                        />
                                    </>
                                )} */}
                            </Grid2>
                        </Grid2>
                    </>
                )}
                {isRequiredOutOfYear && (
                    <>
                        <div className="divider mt-8 mb-8" />
                        <div className="flex align-center justify-between gap-4">
                            <div>
                                <Typography variant="h4" component="div" className="text-primary">
                                    {t("studentDetails.form.outOfYear")}
                                </Typography>
                                <Typography variant="body1" component="div" className="">
                                    {t("studentDetails.form.outOfYearSubtitle")}
                                </Typography>
                                <a
                                    href={OutOfYearTemplate}
                                    className="transcripts-upload__download mt-8 text-link"
                                >
                                    {t("studentDetails.form.outOfYearTemplate")}
                                </a>
                            </div>
                        </div>
                        <Controller
                            name="outOfYearDocument"
                            control={control}
                            render={({ field }) => (
                                <FileDropUploader
                                    className="mt-4"
                                    uploadTextElement={(
                                        <>
                                            <Typography variant="h4" component="div" className="text-primary">
                                                {t("uploader.dragPrUpload")}
                                            </Typography>
                                            <Typography variant="body1" component="div" className="mt-4">
                                                {t("studentDetails.form.documentsAccepts")}
                                            </Typography>
                                        </>
                                    )}
                                    accept={{
                                        "image/jpeg": [".png", ".jpg", ".jpeg"],
                                        "application/pdf": [".pdf"],
                                        "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                    }}
                                    uploadedFiles={uploadedOutOfYearFile}
                                    showFiles
                                    onChange={onChangeOutOfYear}
                                    disabled={isDisabled}
                                />
                            )}
                        />
                    </>
                )}
                {!isVersionUS && (
                    <>
                        <div className="divider mt-8 mb-8" />
                        <Typography variant="h4" component="div" className="text-primary">
                            {t("studentDetails.form.keepSafe")}
                        </Typography>
                        <Typography variant="body1" component="div" className="">
                            {t("studentDetails.form.keepSafeSubtitle")}
                        </Typography>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, md: 8 }} className="mt-4">
                                <Controller
                                    name="safeguarding"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioOptions
                                            options={safeguardingTypesOptions}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors?.safeguarding?.message}
                                            disabled={isDisabled || isLoadingSafeguarding}
                                        />
                                    )}
                                />
                            </Grid2>
                            {(safeguardingOption && safeguardingOption !== "None") && (
                                <Grid2 size={{ xs: 12, md: 8 }} className="mt-4">
                                    {safeguardingFileStatus.key !== KEY_NONE && (
                                        <VerificationStatusChip status={safeguardingFileStatus} />
                                    )}
                                    <Controller
                                        name="safeguardingDocument"
                                        control={control}
                                        render={({ field }) => (
                                            <FileDropUploader
                                                className="mt-4"
                                                accept={{
                                                    "image/jpeg": [".png", ".jpg", ".jpeg"],
                                                    "application/pdf": [".pdf"],
                                                    "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                                }}
                                                uploadedFiles={uploadedSafeguardingFile}
                                                showFiles
                                                onChange={(files) => setSafeguardingFile(files?.[0])}
                                                disabled={isDisabled || isUploadingSafeguardingFile || safeguardingFileStatus.key === KEY_PROGRESS}
                                            />
                                        )}
                                    />
                                    {(isSubmitted && safeguardingOption && safeguardingOption !== "None" && !safeguardingFile && uploadedSafeguardingFile === null) && (
                                        <Typography variant="body1" component="div" className="mt-2 text-base text-error">
                                            {t("formErrors.required")}
                                        </Typography>
                                    )}
                                </Grid2>
                            )}
                        </Grid2>
                    </>
                )}
                <div className="divider mt-8 mb-8" />
                <div className="flex xs:flex-column align-center justify-between gap-4">
                    <div>
                        <Typography variant="h4" component="div" className="text-primary">
                            {t("studentDetails.form.proofOfIdentity")}
                        </Typography>
                        <Typography variant="body1" component="div" className="">
                            {t("studentDetails.form.proofOfIdentitySubtitle")}
                        </Typography>
                    </div>
                    {studentVerificationKey.key !== KEY_NONE && (
                        <VerificationStatusChip status={studentVerificationKey} />
                    )}
                </div>
                <FileDropUploader
                    className="mt-4"
                    uploadTextElement={(
                        <>
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("studentDetails.form.pleaseAttachDocument")}
                            </Typography>
                            <Typography variant="body1" component="div" className="mt-4">
                                {t("studentDetails.form.documentsAccepts")}
                            </Typography>
                        </>
                    )}
                    accept={{
                        "image/jpeg": [".png", ".jpg", ".jpeg"],
                        "application/pdf": [".pdf"],
                        "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                    }}
                    showFiles
                    onChange={onChangeProofOfId}
                    disabled={isDisabled || studentVerificationKey.isInfo || studentVerificationKey.isSuccess}
                />
                {isRequiredDBS && (
                    <>
                        <div className="divider mt-8 mb-8" />
                        <div className="flex align-center justify-between gap-4">
                            <div>
                                <Typography variant="h4" component="div" className="text-primary">
                                    {t("studentDetails.form.DBSCheck")}
                                </Typography>
                                <Typography variant="body1" component="div" className="">
                                    {t("studentDetails.form.DBSCheckSubtitle")}
                                </Typography>
                                <a
                                    href="https://www.gov.uk/request-copy-criminal-record"
                                    target="_blank"
                                    className="transcripts-upload__download mt-8 text-link"
                                    rel="noreferrer"
                                >
                                    {t("studentDetails.form.DBSCheckWebsite")}
                                </a>
                            </div>
                        </div>
                        <Controller
                            name="dbsDocument"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FileDropUploader
                                        className="mt-4"
                                        accept={{
                                            "image/jpeg": [".png", ".jpg", ".jpeg"],
                                            "application/pdf": [".pdf"],
                                            "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                                        }}
                                        uploadedFiles={uploadedDBSFile}
                                        showFiles
                                        onChange={onChangeDBS}
                                        disabled={isDisabled || isUploadingDBS}
                                    />
                                    {(!field.value && !uploadedDBSFile) && (
                                        <Typography variant="body1" component="div" className="mt-2 text-base text-error">
                                            {t("formErrors.required")}
                                        </Typography>
                                    )}
                                </>
                            )}
                        />
                    </>
                )}
                <Button
                    variant="contained"
                    className="button-accent student-details-form__button mt-8"
                    type="submit"
                    loading={isUpdatingStudent || isUploadingProofOfId || isUploadingDBS || isUploadingOutOfYear || isUploadingSenFile}
                    disabled={isDisabled}
                >
                    {t("studentDetails.form.save")}
                </Button>
            </form>
            <BaseDialog
                open={showAddContactDialog}
                onClose={onCloseAddContactDialog}
                title={t("studentDetails.addNewResponsibleAdult")}
                isFullSize
            >
                <ContactInformationForm
                    data={dataForm}
                    isShortForm
                    relationshipsOptions={relationshipsOptions}
                    isLoadingRelations={isLoadingRelations}
                    isSaving={isCreatingContact}
                    onSave={onCreateContact}
                    onCancel={onCloseAddContactDialog}
                />
            </BaseDialog>
            <BaseDialog
                open={showCancelDialog || blocker.state === "blocked"}
                onClose={onCancelDialogClose}
                title={t("studentDetails.form.cancelDialogTitle")}
                withCloseButton
            >
                <div className="flex align-center gap-8">
                    <Button
                        variant="outlined"
                        className="contact-information-form__button"
                        onClick={onCancelDialogClose}
                    >
                        {t("button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent contact-information-form__button"
                        onClick={onCancelDialogContinue}
                    >
                        {t("button.continue")}
                    </Button>
                </div>
            </BaseDialog>
            <SnackbarToast
                open={isSuccessUpdateStudent || errorUpdateStudent}
                isSuccess={isSuccessUpdateStudent}
                isError={errorUpdateStudent}
                onClose={resetUpdateStudent}
            >
                {isSuccessUpdateStudent
                    ? t("studentDetails.form.successMessage")
                    : `${t("studentDetails.form.errorMessage")} (status ${errorUpdateStudent?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessCreateContact || !!errorCreateContact}
                isSuccess={isSuccessCreateContact}
                isError={errorCreateContact}
                onClose={resetCreateContact}
            >
                {isSuccessCreateContact
                    ? t("responsibleAdults.contactInformation.successCreateMessage")
                    : `${t("responsibleAdults.contactInformation.errorCreateMessage")} (status ${errorCreateContact?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUploadProofOfId || !!errorUploadProofOfId}
                isSuccess={isSuccessUploadProofOfId}
                isError={errorUploadProofOfId}
                onClose={resetUploadProofOfId}
            >
                {isSuccessUploadProofOfId
                    ? t("studentDetails.form.successUploadProofOfIdMessage")
                    : `${t("studentDetails.form.errorUploadProofOfIdMessage")} (status ${errorUploadProofOfId?.response?.status})`}
            </SnackbarToast>
            {isRequiredDBS && (
                <SnackbarToast
                    open={isSuccessUploadDBS || !!errorUploadDBS}
                    isSuccess={isSuccessUploadDBS}
                    isError={errorUploadDBS}
                    onClose={resetDBS}
                >
                    {isSuccessUploadDBS
                        ? t("studentDetails.form.successUploadDBSMessage")
                        : `${t("studentDetails.form.errorUploadDBSMessage")} (status ${errorUploadDBS?.response?.status})`}
                </SnackbarToast>
            )}
            { isRequiredOutOfYear && (
                <SnackbarToast
                    open={isSuccessUploadOutOfYear || !!errorUploadOutOfYear}
                    isSuccess={isSuccessUploadOutOfYear}
                    isError={errorUploadOutOfYear}
                    onClose={resetOutOfYear}
                >
                    {isSuccessUploadOutOfYear
                        ? t("studentDetails.form.successUploadOutOfYearMessage")
                        : `${t("studentDetails.form.errorUploadOutOfYearMessage")} (status ${errorUploadOutOfYear?.response?.status})`}
                </SnackbarToast>
            )}
            <SnackbarToast
                open={isSuccessUploadSenFile || !!errorUploadSenFile}
                isSuccess={isSuccessUploadSenFile}
                isError={errorUploadSenFile}
                onClose={resetUploadSenFile}
            >
                {isSuccessUploadSenFile
                    ? t("studentDetails.form.successUploadSenFileMessage")
                    : `${t("studentDetails.form.errorUploadSenFileMessage")} (status ${errorUploadSenFile?.response?.status})`}
            </SnackbarToast>
            <SnackbarToast
                open={isSuccessUploadSafeguardingFile || !!errorUploadSafeguardingFile}
                isSuccess={isSuccessUploadSafeguardingFile}
                isError={errorUploadSafeguardingFile}
                onClose={resetUploadSafeguardingFile}
            >
                {isSuccessUploadSafeguardingFile
                    ? t("studentDetails.form.successUploadSafeguardingFileMessage")
                    : `${t("studentDetails.form.errorUploadSafeguardingFileMessage")} (status ${errorUploadSafeguardingFile?.response?.status})`}
            </SnackbarToast>
        </Preloader>
    );
};

export default StudentDetailsForm;
